// /client/src/pages/Admin/DashboardAdherents/Dashboard.jsx
import React, { useState } from "react";
import StatisticsCards from "./StatisticsCards";
import GroupDistributionChart from "./GroupDistributionChart";
import GenderDistributionChart from "./GenderDistributionChart";
import AgeDistributionChart from "./AgeDistributionChart";
import CommunityStatisticsTable from "./CommunityStatisticsTable";
import CourseStatisticsTable from "./CourseStatisticsTable";
import CommuneDistributionChart from "./CommuneDistributionChart";
import LatestMembers from "./LatestMembers";
import VisitesLineChart from "./VisitesLineChart";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import AdherentStatusBreakdownCard from "./AdherentStatusBreakdownCard";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import GenderAgeDistributionChart from "./GenderAgeDistributionChart";

// Icônes FA
import {
  FaChartLine,
  FaUsersCog,
  FaVenusMars,
  FaTable,
  FaMapMarkedAlt,
  FaChalkboardTeacher,
} from "react-icons/fa";

/* Mini tuile réutilisable */
const MiniTile = ({ icon, title, subtitle, onOpen }) => (
  <button
    onClick={onOpen}
    className="text-left rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-violet-400"
  >
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white grid place-items-center text-lg">
        {icon}
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-400">
          {subtitle}
        </div>
      </div>
    </div>
  </button>
);

/* Modale générique */
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60]">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-0 p-4 sm:p-6 flex items-center justify-center">
        <div className="w-full max-w-6xl rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-900">
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white text-lg sm:text-xl font-bold">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="text-white/90 hover:text-white text-xl leading-none px-2"
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
          </div>
          <div className="p-5 sm:p-6">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // (Réservé si tu veux forcer des valeurs dans StatisticsCards)
  const [adherentsCount] = useState(0);
  const [inscritsCount] = useState(0);
  const [usersCount] = useState(0);

  // Ouverture modales
  const [openLatest, setOpenLatest] = useState(false);
  const [openVisites, setOpenVisites] = useState(false);
  const [openGroups, setOpenGroups] = useState(false);
  const [openGenderAge, setOpenGenderAge] = useState(false);
  const [openCommunityStats, setOpenCommunityStats] = useState(false);
  const [openCommuneChart, setOpenCommuneChart] = useState(false);
  const [openCourseStats, setOpenCourseStats] = useState(false);

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Statistiques des adhérents"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Vue d’ensemble des adhérents (inscriptions, répartition,
                communes, visites, derniers inscrits)
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Cliquez sur une miniature pour ouvrir le graphique/le tableau en
                grand.
              </p>
            </div>
          </div>

          {/* Cartes KPIs */}
          <div className="px-5 pt-5 sm:px-8 space-y-4">
            <StatisticsCards
              adherentsCount={adherentsCount}
              inscritsCount={inscritsCount}
              usersCount={usersCount}
            />
            <AdherentStatusBreakdownCard />
            <LatestMembers />
          </div>

          {/* Grille de miniatures */}
          <div className="px-5 pb-6 sm:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              <MiniTile
                icon={<FaChartLine />}
                title="Visites (ligne)"
                subtitle="Tendance des visites"
                onOpen={() => setOpenVisites(true)}
              />
              <MiniTile
                icon={<FaUsersCog />}
                title="Répartition par cours"
                subtitle="Adhérents par groupes"
                onOpen={() => setOpenGroups(true)}
              />
              <MiniTile
                icon={<FaVenusMars />} // déjà mis dans ta grille
                title="Genre & Âge (global)"
                subtitle="Deux donuts dans un seul graphique"
                onOpen={() => setOpenGenderAge(true)}
              />
              <MiniTile
                icon={<FaTable />}
                title="Stats par commune / genre / âge"
                subtitle="Tableau détaillé"
                onOpen={() => setOpenCommunityStats(true)}
              />
              <MiniTile
                icon={<FaMapMarkedAlt />}
                title="Répartition par commune"
                subtitle="Carte/diagramme des communes"
                onOpen={() => setOpenCommuneChart(true)}
              />
              <MiniTile
                icon={<FaChalkboardTeacher />}
                title="Statistiques par cours"
                subtitle="Tableau récapitulatif"
                onOpen={() => setOpenCourseStats(true)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modales */}
      <Modal
        isOpen={openLatest}
        onClose={() => setOpenLatest(false)}
        title="Derniers adhérents inscrits"
      >
        <LatestMembers />
      </Modal>

      <Modal
        isOpen={openVisites}
        onClose={() => setOpenVisites(false)}
        title="Visites — évolution"
      >
        <div className="w-full max-w-5xl mx-auto max-h-[85vh] overflow-auto">
          <VisitesLineChart />
        </div>
      </Modal>

      <Modal
        isOpen={openGroups}
        onClose={() => setOpenGroups(false)}
        title="Répartition des adhérents par cours"
      >
        <div className="w-full max-w-5xl mx-auto max-h-[85vh] overflow-auto">
          <GroupDistributionChart />
        </div>
      </Modal>

      <Modal
        isOpen={openGenderAge}
        onClose={() => setOpenGenderAge(false)}
        title="Répartition par genre & par âge (global)"
      >
        <div className="max-w-6xl mx-auto max-h-[85vh] overflow-auto">
          <GenderAgeDistributionChart />
        </div>
      </Modal>

      <Modal
        isOpen={openCommunityStats}
        onClose={() => setOpenCommunityStats(false)}
        title="Statistiques des adhérents par commune, genre et tranche d'âge"
      >
        <div className="w-full max-w-6xl mx-auto max-h-[85vh] overflow-auto">
          <CommunityStatisticsTable />
        </div>
      </Modal>

      <Modal
        isOpen={openCommuneChart}
        onClose={() => setOpenCommuneChart(false)}
        title="Répartition des adhérents par commune"
      >
        <div className="w-full max-w-5xl mx-auto max-h-[85vh] overflow-auto">
          <CommuneDistributionChart />
        </div>
      </Modal>

      <Modal
        isOpen={openCourseStats}
        onClose={() => setOpenCourseStats(false)}
        title="Statistiques par cours"
      >
        <div className="w-full max-w-6xl mx-auto max-h-[85vh] overflow-auto">
          <CourseStatisticsTable />
        </div>
      </Modal>
    </section>
  );
}
